import { useCallback, useRef, useState, type DragEvent } from 'react';
import { UploadIcon } from '../components/icons';
import { Banner, Button, cx } from '../components/ui';
import { bytes } from '../lib/format';
import type { CompleteVideoResponse } from '../lib/types';
import { PART_SIZE, StreamingUpload, type UploadState, type UploadTransport } from './uploader';

/**
 * Uploading a file that already exists.
 *
 * On /r/:token this is not a fallback — it is how iPhone screen recordings
 * arrive, and it sits next to the record button as an equal. On /new it is
 * what we offer a browser that cannot capture a screen.
 *
 * It reuses the recorder's multipart uploader wholesale: slice the file into
 * parts, push them through the same queue, get the same retries.
 */

const ACCEPTED_EXTENSIONS = ['.mp4', '.mov', '.webm', '.m4v'];

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};

/** The API accepts exactly three content types; everything else is a 400. */
export function contentTypeFor(file: File): string | null {
  const declared = file.type.toLowerCase();
  if (declared === 'video/mp4' || declared === 'video/quicktime' || declared === 'video/webm') {
    return declared;
  }
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return CONTENT_TYPE_BY_EXTENSION[extension] ?? null;
}

/** Metadata duration, best effort — a wrong guess is overwritten by transcode. */
async function readDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (value: number | undefined) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      done(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : undefined);
    video.onerror = () => done(undefined);
    window.setTimeout(() => done(undefined), 5000);
    video.src = url;
  });
}

export interface FileUploadProps {
  transport: UploadTransport;
  makeTitle: (file: File) => string;
  maxBytes?: number;
  workspace?: 'personal' | 'team';
  orgId?: string | null;
  onUploaded: (result: CompleteVideoResponse) => void;
  compact?: boolean;
}

export function FileUpload({
  transport,
  makeTitle,
  maxBytes,
  workspace,
  orgId,
  onUploaded,
  compact = false,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<UploadState | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File) => {
      setError(null);

      const contentType = contentTypeFor(file);
      if (!contentType) {
        setError(
          `Slate takes MP4, MOV, and WebM files. "${file.name}" does not look like any of those.`,
        );
        return;
      }
      if (maxBytes && file.size > maxBytes) {
        setError(`That file is ${bytes(file.size)}. The limit is ${bytes(maxBytes)}.`);
        return;
      }
      if (file.size === 0) {
        setError('That file is empty.');
        return;
      }

      setBusy(true);
      setFileName(file.name);

      const uploader = new StreamingUpload(transport, setProgress);
      try {
        const duration = await readDuration(file);
        void uploader.start({
          contentType,
          title: makeTitle(file),
          partCount: Math.min(100, Math.max(1, Math.ceil(file.size / PART_SIZE))),
          ...(workspace ? { workspace } : {}),
          ...(orgId ? { orgId } : {}),
        });

        // Slice rather than read: the browser keeps the bytes on disk and the
        // uploader only ever holds one part in memory at a time.
        for (let offset = 0; offset < file.size; offset += PART_SIZE) {
          uploader.push(file.slice(offset, Math.min(offset + PART_SIZE, file.size)));
        }

        const result = await uploader.finish({
          ...(duration !== undefined ? { durationSec: duration } : {}),
        });
        onUploaded(result);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'The upload failed.');
      } finally {
        setBusy(false);
      }
    },
    [makeTitle, maxBytes, onUploaded, orgId, transport, workspace],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files.item(0);
    if (file) void upload(file);
  };

  const percent =
    progress && progress.capturedBytes > 0
      ? Math.min(100, Math.round((progress.uploadedBytes / progress.capturedBytes) * 100))
      : 0;

  return (
    <div className="space-y-3">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cx(
          'rounded-md border border-dashed px-5 text-center transition-colors duration-120',
          compact ? 'py-6' : 'py-10',
          dragging ? 'border-signal bg-signal/[0.06]' : 'border-hairline bg-slate-900',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={`video/mp4,video/quicktime,video/webm,${ACCEPTED_EXTENSIONS.join(',')}`}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.item(0);
            if (file) void upload(file);
            event.target.value = '';
          }}
        />

        {busy ? (
          <div className="space-y-2">
            <p className="truncate text-body text-chalk">{fileName}</p>
            <div className="mx-auto h-1 w-full max-w-sm overflow-hidden rounded-full bg-slate-800">
              <div
                className="h-full bg-signal transition-[width] duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="tnum text-caption text-chalk-faint">
              {percent}% uploaded{progress ? ` · ${bytes(progress.uploadedBytes)}` : ''}
            </p>
          </div>
        ) : (
          <>
            <UploadIcon size={22} className="mx-auto mb-3 text-chalk-faint" />
            <p className="text-body text-chalk">Drop a video here</p>
            <p className="mx-auto mt-1 max-w-sm text-caption text-chalk-faint">
              MP4, MOV, or WebM{maxBytes ? ` up to ${bytes(maxBytes)}` : ''}. A screen recording
              straight off an iPhone works fine.
            </p>
            <Button className="mt-4" onClick={() => inputRef.current?.click()}>
              Choose a file
            </Button>
          </>
        )}
      </div>

      {error ? <Banner tone="danger" title="That did not upload">{error}</Banner> : null}
    </div>
  );
}
